/**
 * Embed Question Composer
 * Input field and send button for asking questions in the embed widget
 * Uses centralized theme system and advanced config for send button customization
 */

import React, { useCallback, useRef, useState } from 'react';

import { useAdvancedConfig } from '../hooks/useAdvancedConfig';
import { useEmbedTheme } from '../theme';

// ============================================================================
// Types
// ============================================================================

interface EmbedSendOptions {
  forceWebSearch?: boolean;
}

interface EmbedQuestionComposerProps {
  onSend: (message: string, options?: EmbedSendOptions) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  // Surfaces that don't pipe through the cognition turn stream (e.g. skill marketplace)
  // should leave this off so the toggle isn't shown as a no-op.
  showWebSearchToggle?: boolean;
}

// ============================================================================
// Globe Icon — toggles per-turn force_web_search opt-in.
// ============================================================================

const GlobeIcon: React.FC<{ color: string; size?: number }> = ({ color, size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

// ============================================================================
// Send Icons - Multiple styles based on advanced config
// ============================================================================

const ArrowIcon: React.FC<{ color: string; size?: number }> = ({ color, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const SendPlaneIcon: React.FC<{ color: string; size?: number }> = ({ color, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
  </svg>
);

const PaperPlaneIcon: React.FC<{ color: string; size?: number }> = ({ color, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const SendIcon: React.FC<{ iconStyle: 'arrow' | 'send' | 'paper-plane'; color: string; size?: number }> = ({
  iconStyle,
  color,
  size = 16,
}) => {
  switch (iconStyle) {
    case 'send':
      return <SendPlaneIcon color={color} size={size} />;
    case 'paper-plane':
      return <PaperPlaneIcon color={color} size={size} />;
    case 'arrow':
    default:
      return <ArrowIcon color={color} size={size} />;
  }
};

// ============================================================================
// Main Component
// ============================================================================

export const EmbedQuestionComposer: React.FC<EmbedQuestionComposerProps> = ({
  onSend,
  placeholder = 'Type your message...',
  disabled = false,
  isLoading = false,
  showWebSearchToggle = false,
}) => {
  const { theme, styles, utils, primaryColor } = useEmbedTheme();
  const { styles: advancedStyles, config: advancedConfig } = useAdvancedConfig();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [forceWebSearch, setForceWebSearch] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || isSending || disabled || isLoading) return;

    // Clear the input synchronously so the user gets immediate feedback —
    // resolving the thread and opening the SSE stream can take 200-800ms,
    // and waiting on the await before clearing makes the composer feel laggy.
    setMessage('');
    setForceWebSearch(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsSending(true);
    try {
      await onSend(trimmedMessage, forceWebSearch ? { forceWebSearch: true } : undefined);
    } catch (error) {
      console.error('[EmbedComposer] Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  }, [message, onSend, isSending, disabled, isLoading, forceWebSearch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, []);

  // Check if send button should be visible based on advanced config
  const hasText = message.trim().length > 0;
  const showSendButton = advancedStyles.sendButtonShowOnlyWithText ? hasText : true;
  const canSend = hasText && !isSending && !disabled && !isLoading;

  // Get send button size from advanced config
  const buttonSize = advancedConfig.sendButton?.size || 32;
  const iconSize = Math.round(buttonSize * 0.5);

  return (
    <div
      style={{
        padding: `${advancedConfig.spacing?.inputPadding || 12}px`,
        backgroundColor: theme.colors.background.surface,
      }}
    >
      <div
        style={utils.merge(styles.flex({ align: 'center', gap: '2' }), {
          backgroundColor:
            theme.mode === 'dark' ? theme.colors.background.input : theme.colors.background.subtle,
          borderRadius: theme.borderRadius.full,
          padding: '6px 6px 6px 12px',
          border: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
        })}
      >
        {/* Web search toggle — opts the next turn into force_web_search.
            Off by default; resets to off after each send. */}
        {showWebSearchToggle && (
          <button
            type="button"
            onClick={() => setForceWebSearch((v) => !v)}
            disabled={disabled || isLoading}
            title={forceWebSearch ? 'Web search on — click to disable' : 'Search the web'}
            aria-pressed={forceWebSearch}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: theme.borderRadius.full,
              border: 'none',
              backgroundColor: forceWebSearch ? `${primaryColor}1f` : 'transparent',
              color: forceWebSearch ? primaryColor : theme.colors.text.tertiary,
              cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
              opacity: disabled || isLoading ? 0.5 : 1,
              flexShrink: 0,
              transition: `all ${theme.animation.duration.fast}`,
            }}
          >
            <GlobeIcon color={forceWebSearch ? primaryColor : theme.colors.text.tertiary} />
          </button>
        )}

        {/* Text Input - using advanced typography config */}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          rows={1}
          style={{
            flex: 1,
            minHeight: '32px',
            maxHeight: '120px',
            resize: 'none',
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            color: theme.colors.text.primary,
            fontSize: `${advancedConfig.typography?.inputFontSize || 14}px`,
            lineHeight: '32px',
            fontFamily: advancedStyles.typography.fontFamily,
            padding: 0,
          }}
        />

        {/* Send Button - using advanced config styles */}
        {showSendButton && (
          <button
            onClick={handleSend}
            disabled={!canSend}
            style={{
              ...advancedStyles.sendButton,
              backgroundColor: canSend
                ? advancedStyles.sendButton.backgroundColor
                : theme.colors.background.elevated,
              cursor: canSend ? 'pointer' : 'not-allowed',
              opacity: canSend ? 1 : 0.5,
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (canSend) {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = canSend ? '1' : '0.5';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isSending ? (
              <div
                style={{
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  border: '2px solid transparent',
                  borderTopColor:
                    advancedConfig.sendButton?.variant === 'filled'
                      ? theme.colors.text.inverse
                      : primaryColor,
                  borderRadius: theme.borderRadius.full,
                  animation: 'embed-spin 0.8s linear infinite',
                }}
              />
            ) : (
              <SendIcon
                iconStyle={advancedStyles.sendButtonIcon}
                color={
                  canSend
                    ? advancedConfig.sendButton?.variant === 'filled'
                      ? theme.colors.text.inverse
                      : primaryColor
                    : theme.colors.text.tertiary
                }
                size={iconSize}
              />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default EmbedQuestionComposer;
